/**
 * lsg-subscription-per-day.js
 *
 * Three subscription UI enhancements on the Stay AI widget, all active ONLY when
 * Subscribe & Save is the selected purchase option:
 *
 *   1. Size-pill cost-per-day — selected plan's price ÷ that plan's delivery interval,
 *      written ONLY to the pill matching the widget's current variant.
 *   2. Cadence line under the frequency dropdown ("Delivered Every 1.5 Months").
 *   3. Dog-size icon as the first flex child of the subscription card, swapping with
 *      the selected dog size.
 *
 * Self-contained by design: does NOT rely on server-rendered data-* attributes on the
 * Stay options — those don't survive Stay's client re-render and aren't present in
 * every render path. Interval/cadence maps live here (dog-size plans are fixed); price
 * is read from Stay's own live price element; icons come from a metaobject-backed JSON
 * blob emitted by snippets/vinny-dog-size-icons.liquid.
 */
(function () {
  'use strict';

  // selling_plan.id → delivery interval in days.
  //
  // Grouped by pack size. Intervals are NOT exposed to Liquid by Stay AI, so they are
  // recorded here from each plan's billing schedule. Adding a new selling plan requires
  // adding its id to BOTH maps below and creating a matching dog_size_icon metaobject entry.
  var PLAN_DAYS = {
    // ── 120 ct plans ──
    '9619538080': 45,   // Large  (46-80Lbs)
    '9619570848': 30,   // XLarge (80+Lbs)
    '9619603616': 60,   // Medium (21-41Lbs)
    '9619636384': 120,  // Small  (Up-to 20Lbs)
    // ── 60 ct plans ──
    '9621668000': 20,   // Large  (46-80Lbs)
    '9621635232': 15,   // XLarge (80+Lbs)
    '9621700768': 30,   // Medium (21-41Lbs)
    '9621733536': 60    // Small  (Up-to 20Lbs)
  };

  // selling_plan.id → cadence label shown under the dropdown.
  var PLAN_NAME = {
    // ── 120 ct plans ──
    '9619538080': 'Delivered Every 1.5 Months',
    '9619570848': 'Delivered Every 1 Month',
    '9619603616': 'Delivered Every 2 Months',
    '9619636384': 'Delivered Every 4 Months',
    // ── 60 ct plans ──
    '9621668000': 'Delivered Every 20 Days',
    '9621635232': 'Delivered Every 15 Days',
    '9621700768': 'Delivered Every 30 Days',
    '9621733536': 'Delivered Every 60 Days'
  };

  var CURRENCY_SYMBOL = '$';
  var CADENCE_CLASS = 'lsg-cadence-line';
  var ICON_CLASS = 'lsg-dog-size-icon';

  // plan id → icon URL, from the metaobject JSON. Empty object if snippet absent.
  var PLAN_ICONS = (function () {
    var el = document.getElementById('lsg-dog-size-icons');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; }
    catch (e) { return {}; }
  })();

  function formatPerDay(cents, days) {
    if (!days || days <= 0 || isNaN(cents)) return null;
    return CURRENCY_SYMBOL + (cents / 100 / days).toFixed(2) + ' / day';
  }

  function getWidget() {
    return document.querySelector('purchase-options[data-stayai-subscription-widget]');
  }

  function isSubActive(widget) {
    var sub = widget.querySelector('#purchaseTypeSubscription');
    return !!(sub && sub.checked);
  }

  function getSelectedPlanId(widget) {
    var sel = widget.querySelector('select[name="stayai_selling_plan"]');
    if (!sel) return null;
    var opt = sel.options[sel.selectedIndex];
    if (!opt || opt.value === '') return null;
    return opt.value;
  }

  // The variant the Stay widget currently represents. Used to scope the per-day
  // figure so a variant on a different (or no) selling plan never shows a number.
  function getCurrentVariantId(widget) {
    return widget.getAttribute('currentVariantId') ||
           widget.getAttribute('currentvariantid') || null;
  }

  function getSubPriceCents(widget) {
    var el = widget.querySelector('[data-stayai-subscription-price]');
    if (!el) return NaN;
    var clone = el.cloneNode(true);
    clone.querySelectorAll('s').forEach(function (s) { s.remove(); });
    var matches = (clone.textContent || '').match(/[\d,]+\.?\d*/g);
    if (!matches || !matches.length) return NaN;
    var dollars = parseFloat(matches[matches.length - 1].replace(/,/g, ''));
    return isNaN(dollars) ? NaN : Math.round(dollars * 100);
  }

  // Cadence line: created once, placed directly after the frequency dropdown box,
  // inside the subscription label (above the benefits row).
  function ensureCadenceLine(widget) {
    var box = widget.querySelector('[data-retextion-subscription-box]');
    if (!box || !box.parentNode) return null;
    var line = box.parentNode.querySelector('.' + CADENCE_CLASS);
    if (!line) {
      line = document.createElement('div');
      line.className = CADENCE_CLASS;
      line.style.cssText = 'margin:6px 0 0;font-size:13px;line-height:1.2;';
      box.insertAdjacentElement('afterend', line);
    }
    return line;
  }

  // Dog-size icon.
  //
  // The yellow card border lives on .stayai-purchase-label__wrapper, so the icon must go
  // INSIDE that element to sit within the border. We do NOT re-flex the wrapper directly
  // (that collapses the card into one row). Instead we move the wrapper's existing children
  // into a content column, then lay out [icon | content] inside the wrapper.
  function ensureIcon(widget) {
    var label = widget.querySelector('label[for="purchaseTypeSubscription"]');
    if (!label) return null;
    var wrapper = label.querySelector('.stayai-purchase-label__wrapper');
    if (!wrapper) return null;

    // Undo the previous outside-the-border layout if it's still present.
    var oldRow = label.querySelector('.' + ICON_CLASS + '-row');
    if (oldRow && oldRow.parentNode) {
      oldRow.parentNode.insertBefore(wrapper, oldRow);
      wrapper.style.flex = '';
      wrapper.style.minWidth = '';
      oldRow.parentNode.removeChild(oldRow);
    }

    var icon = wrapper.querySelector(':scope > .' + ICON_CLASS);
    if (!icon) {
      // Move everything currently in the wrapper into a content column.
      var content = document.createElement('div');
      content.className = ICON_CLASS + '-content';
      content.style.cssText = 'flex:1 1 auto;min-width:0;';
      while (wrapper.firstChild) content.appendChild(wrapper.firstChild);

      icon = document.createElement('div');
      icon.className = ICON_CLASS;
      icon.style.cssText = [
        'flex:0 0 auto',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'width:72px',
        'margin-right:12px',
        'overflow:hidden'
      ].join(';');

      var img = document.createElement('img');
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.style.cssText = 'max-width:100%;height:auto;display:block;';
      icon.appendChild(img);

      wrapper.appendChild(icon);
      wrapper.appendChild(content);

      // Wrapper becomes the row. Its own children are now just [icon, content],
      // so this does not disturb the card's internal vertical layout.
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'stretch';

      // Remember Stay's original left padding so it can be restored when no icon shows.
      if (wrapper.dataset.lsgPadLeft === undefined) {
        wrapper.dataset.lsgPadLeft = getComputedStyle(wrapper).paddingLeft || '';
      }
    }
    return icon;
  }

  function update() {
    var widget = getWidget();
    if (!widget) return;

    var active = isSubActive(widget);
    var planId = active ? getSelectedPlanId(widget) : null;
    var days = planId ? PLAN_DAYS[planId] : null;
    var name = planId ? PLAN_NAME[planId] : null;
    var iconSrc = planId ? PLAN_ICONS[planId] : null;
    var variantId = getCurrentVariantId(widget);

    // 1. Per-day — ONLY on the pill for the widget's current variant. Every other
    //    pill is cleared, so a variant on a different/absent selling plan shows nothing.
    var perDayText = (active && days) ? formatPerDay(getSubPriceCents(widget), days) : null;
    document.querySelectorAll('.lsg-buybox__per-day').forEach(function (span) {
      var spanVariant = span.getAttribute('data-per-day-variant');
      var isCurrent = variantId && spanVariant && String(spanVariant) === String(variantId);
      if (perDayText && isCurrent) {
        span.textContent = perDayText;
        span.classList.remove('hidden');
      } else {
        span.textContent = '';
        span.classList.add('hidden');
      }
    });

    // 2. Cadence line
    var line = ensureCadenceLine(widget);
    if (line) {
      if (active && name) { line.textContent = name; line.style.display = ''; }
      else { line.textContent = ''; line.style.display = 'none'; }
    }

    // 3. Dog-size icon
    var icon = ensureIcon(widget);
    if (icon) {
      var img = icon.querySelector('img');
      var wrapper = icon.parentNode;
      if (active && iconSrc) {
        if (img.getAttribute('src') !== iconSrc) img.setAttribute('src', iconSrc);
        icon.style.display = 'flex';
        // Icon supplies the left inset; drop Stay's own left padding so the
        // silhouette sits flush against the card edge.
        if (wrapper) wrapper.style.paddingLeft = '0px';
      } else {
        icon.style.display = 'none';
        // No icon — restore Stay's original left padding.
        if (wrapper) wrapper.style.paddingLeft = wrapper.dataset.lsgPadLeft || '';
      }
    }
  }

  function bind(widget) {
    if (widget.__lsgSubEnhBound) return;
    widget.__lsgSubEnhBound = true;

    widget.querySelectorAll('input[name="purchaseType"]').forEach(function (r) {
      r.addEventListener('change', update);
    });

    var sel = widget.querySelector('select[name="stayai_selling_plan"]');
    if (sel) sel.addEventListener('change', update);

    // Stay toggles is-selected via its own JS; re-read on the next tick after a click.
    widget.querySelectorAll('.stayai-purchase-label').forEach(function (l) {
      l.addEventListener('click', function () { setTimeout(update, 0); });
    });

    // Stay re-renders parts of the widget (price, select, whole subtree) on variant
    // and plan changes. Recompute on mutation, debounced to one frame.
    var scheduled = false;
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; update(); });
    });
    mo.observe(widget, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['currentvariantid', 'currentVariantId']
    });

    update();
  }

  // Stay replaces the entire <purchase-options> element on variant change (it tears the
  // widget down and rebuilds it — see its "No SPs, hiding widget" path). The rebuilt node
  // is a CLONE, so it carries copies of our injected icon/cadence nodes but none of our
  // listeners. Detect the swap, strip the cloned injections so they are rebuilt against
  // the live node, and rebind.
  function adopt(widget) {
    if (!widget || widget.__lsgSubEnhBound) return;

    // Remove cloned-in artifacts from the previous element; ensureIcon/ensureCadenceLine
    // recreate them fresh. Unwrap the icon content column back into the wrapper first so
    // we don't nest columns on every rebuild.
    var wrapper = widget.querySelector('.stayai-purchase-label__wrapper');
    if (wrapper) {
      var content = wrapper.querySelector(':scope > .' + ICON_CLASS + '-content');
      if (content) {
        while (content.firstChild) wrapper.insertBefore(content.firstChild, content);
        content.parentNode.removeChild(content);
      }
      var staleIcon = wrapper.querySelector(':scope > .' + ICON_CLASS);
      if (staleIcon) staleIcon.parentNode.removeChild(staleIcon);
      wrapper.style.display = '';
      wrapper.style.alignItems = '';
      wrapper.style.paddingLeft = '';
      delete wrapper.dataset.lsgPadLeft;
    }
    var staleCadence = widget.querySelector('.' + CADENCE_CLASS);
    if (staleCadence && staleCadence.parentNode) {
      staleCadence.parentNode.removeChild(staleCadence);
    }
    var staleRow = widget.querySelector('.' + ICON_CLASS + '-row');
    if (staleRow && staleRow.parentNode) {
      var inner = staleRow.querySelector('.stayai-purchase-label__wrapper');
      if (inner) staleRow.parentNode.insertBefore(inner, staleRow);
      staleRow.parentNode.removeChild(staleRow);
    }

    bind(widget);
  }

  function init() {
    adopt(getWidget());

    // Watch for the widget being swapped in/out of the document.
    var docScheduled = false;
    var docMo = new MutationObserver(function () {
      if (docScheduled) return;
      docScheduled = true;
      requestAnimationFrame(function () {
        docScheduled = false;
        var w = getWidget();
        if (w && !w.__lsgSubEnhBound) {
          adopt(w);
        } else if (w) {
          update();
        }
      });
    });
    docMo.observe(document.body, { childList: true, subtree: true });

    // Variant switches elsewhere in the buybox can land before Stay finishes rebuilding.
    // Re-check on a short settle delay after any pill click.
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('.variant-option-button, [data-variant-id]')) {
        [60, 250, 600].forEach(function (ms) {
          setTimeout(function () {
            var w = getWidget();
            if (w && !w.__lsgSubEnhBound) adopt(w);
            else update();
          }, ms);
        });
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();